import { BACKEND_URL } from "@/lib/backendUrl";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, FileSignature, Loader2 } from "lucide-react";

const API = `${BACKEND_URL}/api`;

const Row = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.09em] text-slate-500">{label}</div>
    <div className="text-[13px] font-medium text-slate-900">{value || "—"}</div>
  </div>
);

const message = (e) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join(" ");
  return "Something went wrong. Please try again.";
};

export default function HireAgreementSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/hire-agreement/${token}`)
      .then((r) => { setData(r.data); if (r.data.status === "signed") setDone(r.data); })
      .catch((e) => setError(message(e)));
  }, [token]);

  const sign = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/public/hire-agreement/${token}/sign`,
        { signed_name: name, agreed });
      setDone(res.data);
    } catch (e) { setError(message(e)); }
    setBusy(false);
  };

  if (error && !data) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-100 px-6">
        <p className="max-w-md text-center text-[13px] text-slate-700" data-testid="agreement-error">
          {error}
        </p>
      </div>
    );
  }
  if (!data) {
    return <div className="grid min-h-screen place-items-center bg-slate-100">
      <Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>;
  }

  const b = data.body;
  const cur = b.money.currency;
  const money = (v) => `${cur} ${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10 text-slate-900">
      <div className="mx-auto max-w-3xl space-y-5">
        <header className="rounded-2xl bg-white p-6 shadow-sm">
          <div className="text-[11px] uppercase tracking-[0.16em] text-indigo-600">
            {b.organisation.name}
          </div>
          <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">Hall hire agreement</h1>
          <p className="mt-1 text-[12px] text-slate-500">
            {data.agreement_ref} · booking {b.booking_ref} · terms version {data.terms_version}
          </p>
        </header>

        <section className="rounded-2xl bg-white p-6 shadow-sm" data-testid="agreement-detail">
          <div className="grid gap-5 sm:grid-cols-3">
            <Row label="Hirer" value={b.hirer.name} />
            <Row label="Contact" value={b.hirer.contact} />
            <Row label="Room" value={b.event.space} />
            <Row label="Event" value={b.event.title} />
            <Row label="From" value={b.event.starts_at.slice(0, 16).replace("T", " ")} />
            <Row label="To" value={b.event.ends_at.slice(11, 16)} />
            <Row label="Hire charge" value={money(b.money.charge)} />
            <Row label={b.money.deposit_refundable ? "Refundable deposit" : "Deposit (part payment)"}
                 value={`${money(b.money.deposit)}${b.money.deposit_on ? ` by ${b.money.deposit_on}` : ""}`} />
            <Row label="People expected" value={b.event.attendees} />
          </div>

          <h2 className="mt-7 text-[11px] uppercase tracking-[0.12em] text-slate-500">
            If you cancel
          </h2>
          <ul className="mt-2 space-y-1 text-[12.5px]">
            {b.cancellation_bands.map((band) => (
              <li key={band.days_before} data-testid={`band-${band.days_before}`}>
                {band.days_before} days' notice or more — {band.charge_percent}% of the hire charge
              </li>
            ))}
          </ul>

          <h2 className="mt-7 text-[11px] uppercase tracking-[0.12em] text-slate-500">Terms of hire</h2>
          <div className="mt-2 space-y-2 text-[12.5px] leading-relaxed text-slate-700">
            {(b.terms || "No additional terms recorded.").split("\n").filter(Boolean)
              .map((line, i) => <p key={i}>{line}</p>)}
          </div>
        </section>

        {done ? (
          <section className="rounded-2xl border border-emerald-300 bg-emerald-50 p-6 shadow-sm"
                   data-testid="agreement-signed">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-emerald-800">
              <CheckCircle2 className="h-4 w-4" /> Signed by {done.signed_name}
            </div>
            <p className="mt-1 text-[12px] text-emerald-900/80">
              {new Date(done.signed_at).toLocaleString()} · we have a copy and will be in touch about
              the deposit and balance.
            </p>
          </section>
        ) : (
          <section className="rounded-2xl bg-white p-6 shadow-sm" data-testid="agreement-sign-form">
            <h2 className="text-[13px] font-semibold">Sign the agreement</h2>
            <label className="mt-4 flex items-start gap-2 text-[12.5px] text-slate-700">
              <input type="checkbox" checked={agreed} data-testid="agreement-agree-checkbox"
                     onChange={(e) => setAgreed(e.target.checked)}
                     className="mt-0.5 h-4 w-4 rounded border-slate-400" />
              I have read and accept the terms of hire, the deposit arrangement and the cancellation
              charges set out above.
            </label>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   data-testid="agreement-name-input" placeholder="Type your full name"
                   className="mt-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-[13px] outline-none focus:border-indigo-500" />
            {error && <p className="mt-3 text-[12px] text-red-600" data-testid="agreement-error">{error}</p>}
            <button type="button" onClick={sign} data-testid="agreement-sign-button"
                    disabled={busy || !agreed || name.trim().length < 3}
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-indigo-600 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                : <FileSignature className="h-4 w-4" />}
              Sign and return
            </button>
            <p className="mt-3 text-[11px] text-slate-500">
              Signing records your name, the time and the version of the terms shown on this page.
            </p>
          </section>
        )}
      </div>
    </div>
  );
}
